import React, { useState } from 'react';

const QuizCodeEntry = ({ onStartQuizGame, onBack }) => {
  const [quizCode, setQuizCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quizCode.trim()) {
      setError('Please enter a quiz code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate the quiz code by making a test request
      const response = await fetch(`/api/quiz/question?quiz_code=${encodeURIComponent(quizCode.trim())}`);
      const data = await response.json();

      if (data.success || (data.quiz_context && data.quiz_context.quiz_mode)) {
        // Quiz code is valid, start the game
        onStartQuizGame(quizCode.trim().toUpperCase());
      } else {
        setError('Quiz code not found. Please check the code and try again.');
      }
    } catch (error) {
      console.error('Error validating quiz code:', error);
      setError('Failed to validate quiz code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6);
    setQuizCode(value);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back button */}
        <button
          onClick={onBack}
          className="mb-6 text-white/80 hover:text-white flex items-center gap-2 transition-colors"
          disabled={loading}
        >
          ← Back to Career Climb
        </button>

        {/* Main card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📚</div>
            <h1 className="text-3xl font-bold text-white mb-2">Teacher Mode</h1>
            <p className="text-white/80">Enter the code provided by your teacher to start the quiz challenge!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="quizCode" className="block text-white font-medium mb-2">
                Quiz Code
              </label>
              <input
                type="text"
                id="quizCode"
                value={quizCode}
                onChange={handleCodeChange}
                placeholder="Enter 6-character code"
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 text-center text-2xl font-mono tracking-wider focus:ring-2 focus:ring-white/50 focus:border-white/50 outline-none transition-all"
                disabled={loading}
                maxLength={6}
                autoComplete="off"
                autoFocus
              />
              {error && (
                <p className="text-red-200 text-sm mt-2 bg-red-500/20 rounded-lg p-2">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!quizCode.trim() || loading}
              className="w-full bg-white text-orange-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-600 border-t-transparent"></div>
                  Validating...
                </div>
              ) : (
                'Start Quiz Challenge'
              )}
            </button>
          </form>

          {/* Help text */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <h3 className="text-white font-medium mb-3">💡 Tips:</h3>
            <ul className="text-white/80 text-sm space-y-2">
              <li>• Quiz codes are 6 characters (letters and numbers)</li>
              <li>• Ask your teacher if you don't have the code</li>
              <li>• You'll only get questions from this specific quiz</li>
              <li>• Your progress will be tracked for your teacher</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizCodeEntry;
